import { useState } from "react";
import { useGetEventsCount } from "../api/useGetEventsCount";
import {
  Page,
  Layout,
  Card,
  Tabs,
  Box,
  SkeletonDisplayText,
  Banner,
  Button,
  BlockStack,
  InlineStack,
  Icon,
  Text,
} from "@shopify/polaris";
import { StarIcon } from "@shopify/polaris-icons";
import { formatNumber } from "../utils/formatNumber";
import { trackEvent } from "../lib/mixpanel";
import { AppEnableDisableButton } from "../components/AppEnableDisableButton";

// Define period options as a constant to avoid duplication
const PERIOD_OPTIONS = ["today", "yesterday", "last7days", "last30days"];

const tabs = [
  {
    id: PERIOD_OPTIONS[0],
    content: "Today",
    accessibilityLabel: "Today",
    panelID: "today-panel",
  },
  {
    id: PERIOD_OPTIONS[1],
    content: "Yesterday",
    accessibilityLabel: "Yesterday",
    panelID: "yesterday-panel",
  },
  {
    id: PERIOD_OPTIONS[2],
    content: "Last 7 Days",
    accessibilityLabel: "Last 7 days",
    panelID: "last7days-panel",
  },
  {
    id: PERIOD_OPTIONS[3],
    content: "Last 30 Days",
    accessibilityLabel: "Last 30 days",
    panelID: "last30days-panel",
  },
];

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card padding="400">
      <BlockStack gap="200" align="center">
        <Text as="p" variant="headingMd">
          {label}
        </Text>
        <Text as="p" variant="heading2xl">
          {formatNumber(value)}
        </Text>
      </BlockStack>
    </Card>
  );
}

export function AnalyticsPage() {
  const [period, setPeriod] = useState(PERIOD_OPTIONS[0]);
  const { data, error, isLoading } = useGetEventsCount({ period });

  const handleTabChange = (selectedTabIndex: number) => {
    setPeriod(PERIOD_OPTIONS[selectedTabIndex]);
  };

  const selectedTabIndex = PERIOD_OPTIONS.indexOf(period);

  function onReviewClick() {
    trackEvent("Review App Clicked");
  }

  const reviewLink = "https://apps.shopify.com/plainchat-whatsapp-support";

  return (
    <Page
      title=" "
      titleMetadata={
        <Tabs
          tabs={tabs}
          selected={selectedTabIndex}
          onSelect={handleTabChange}
        />
      }
    >
      <Layout>
        {isLoading ? (
          <>
            {[0, 1, 2].map((i) => (
              <Layout.Section variant="oneThird" key={i}>
                <Card padding="400">
                  <BlockStack gap="200" align="center">
                    <SkeletonDisplayText size="small" maxWidth="40%" />
                    <SkeletonDisplayText size="medium" maxWidth="60%" />
                  </BlockStack>
                </Card>
              </Layout.Section>
            ))}
          </>
        ) : error ? (
          <Layout.Section>
            <Banner title="Error" tone="critical">
              <p>Something went wrong loading analytics data.</p>
            </Banner>
          </Layout.Section>
        ) : (
          <>
            <Layout.Section variant="oneThird">
              <StatCard label="Views" value={data?.data?.views || 0} />
            </Layout.Section>
            <Layout.Section variant="oneThird">
              <StatCard label="Visitors" value={data?.data?.visitors || 0} />
            </Layout.Section>
            <Layout.Section variant="oneThird">
              <StatCard label="Clicks" value={data?.data?.clicks || 0} />
            </Layout.Section>
          </>
        )}

        <Layout.Section>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Widget
            </Text>
            <AppEnableDisableButton />
          </BlockStack>
        </Layout.Section>
      </Layout>

      {/* Review Request */}
      <Box paddingBlockStart="500">
        <Card padding="400">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Icon source={StarIcon} tone="base" />
              <BlockStack gap="100">
                <Text as="h3" variant="headingSm">
                  Love PlainChat? Leave us a review
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  Your review helps other small businesses find us and keeps the
                  app improving — it only takes 30 seconds.
                </Text>
              </BlockStack>
            </InlineStack>
            <Button
              variant="primary"
              url={reviewLink}
              external
              onClick={onReviewClick}
            >
              Leave a review
            </Button>
          </InlineStack>
        </Card>
      </Box>
    </Page>
  );
}
